import { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Globals } from "../globals";
import { auth, db } from "../services/firebase";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import SundayService, { sundayServiceConverter } from "../DTOs/SundayService";
import Preacher, { preacherConverter } from "../DTOs/Preacher";
import Spinner from "react-bootstrap/esm/Spinner";
import Button from "react-bootstrap/esm/Button";
import Modal from "react-bootstrap/esm/Modal";
import I_IMC_Props from "../infos/I_IMC_Props";
import { deleteItemFromFirestore, getHolidays } from "../services/firestoreService";
import { gemeindeSubPath } from "../services/gemeindePaths";
import { useAuth } from "../hooks/useAuth";
import { useGemeinde } from "../context/GemeindeContext";
import CalendarView from "./CalendarView";
import SwapApprovals from "./SwapApprovals";
import { loadAbsencesByPreacher } from "../services/absenceService";
import { AbsenceMap, findAbsence, formatAbsence } from "../utils/absences";
import { getDeclineReason, getSlotAlert } from "../utils/slotStatus";
import { preacherDisplayName } from "../utils/preachers";
import { canHaveChoir as canHaveChoirFor } from "../utils/serviceType";
import { useChoirServiceTypes } from "../hooks/useChoirServiceTypes";
import ServiceSeriesDialog from "./ServiceSeriesDialog";
import ServiceDetailModal from "./ServiceDetailModal";
import { logger } from "../utils/logger";
import { calendarHolidays } from "../utils/germanHolidays";

interface SundayServiceWithId extends SundayService {
    id: string;
}


export default function SimpleSundayServiceList({ setVariant, setMessage, setShowMessage }: I_IMC_Props) {
    const { t } = useTranslation();
    // Dates in the UI language (GOD-56); short names without the trailing dot ("Okt." → "Okt").
    const locale = t("calendar.locale") as string;
    const shortName = (date: Date, part: "month" | "weekday") =>
        date.toLocaleDateString(locale, { [part]: "short" }).replace(/\.$/, "");
    const navigate = useNavigate();
    const { isAuthenticated, loading: authLoading, canEdit } = useAuth();
    const { activeGemeindeId, activeGemeinde } = useGemeinde();
    // Create several services at once (GOD-47).
    const [showSeries, setShowSeries] = useState(false);
    const [services, setServices] = useState<SundayServiceWithId[]>([]);
    const choirTypes = useChoirServiceTypes(activeGemeindeId, canEdit);
    const [absences, setAbsences] = useState<AbsenceMap>(new Map());
    const [preachers, setPreachers] = useState<Preacher[]>([]);
    const [loading, setLoading] = useState(true);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [serviceToDelete, setServiceToDelete] = useState<string | null>(null);
    const [viewMode, setViewMode] = useState<"list" | "calendar">("list");
    const [currentMonth, setCurrentMonth] = useState(new Date());
    // Read-only detail view for members (GOD-53).
    const [detailService, setDetailService] = useState<SundayServiceWithId | null>(null);
    // Holidays imported from the sermon plan; loaded once the calendar is opened (GOD-50).
    const [importedHolidays, setImportedHolidays] = useState<{ date: string; name: string }[] | null>(null);
    useEffect(() => {
        setImportedHolidays(null);
    }, [activeGemeindeId]);
    useEffect(() => {
        if (viewMode !== "calendar" || !activeGemeindeId || importedHolidays !== null) return;
        let cancelled = false;
        getHolidays(activeGemeindeId)
            .then((list) => !cancelled && setImportedHolidays(list.map((h) => ({ date: h.date, name: h.name }))))
            .catch(() => !cancelled && setImportedHolidays([]));
        return () => {
            cancelled = true;
        };
    }, [viewMode, activeGemeindeId, importedHolidays]);
    // Statutory holidays of the Gemeinde's Bundesland for the shown month (and its neighbours) plus imported ones.
    const holidayRegion = activeGemeinde?.holidayRegion;
    const shownYear = currentMonth.getFullYear();
    const holidays = useMemo(
        () => calendarHolidays([shownYear - 1, shownYear, shownYear + 1], holidayRegion, importedHolidays ?? []),
        [shownYear, holidayRegion, importedHolidays]
    );
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);

    useEffect(() => {
        document.title = t("simpleSundayService.listTitle");
    }, [t]);

    const loadData = useCallback(async () => {
        if (authLoading) return;
        if (!isAuthenticated || !auth.currentUser?.uid) {
            navigate(Globals.PATH_DEFAULT);
            setLoading(false);
            return;
        }
        if (!activeGemeindeId) {
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const preacherSnapshot = await getDocs(
                collection(db, ...gemeindeSubPath(activeGemeindeId, Globals.PREACHER_SUBCOLLECTION)).withConverter(preacherConverter)
            );
            const preacherArray: Preacher[] = [];
            preacherSnapshot.forEach((doc) => preacherArray.push(doc.data()));
            setPreachers(preacherArray);
            // Absences are only readable for admins; for everyone else the map stays empty.
            setAbsences(canEdit ? await loadAbsencesByPreacher(activeGemeindeId, preacherArray.map((p) => p.key)) : new Map());

            const servicesQuery = query(
                collection(db, ...gemeindeSubPath(activeGemeindeId, Globals.SUNDAY_SERVICE_SUBCOLLECTION)).withConverter(sundayServiceConverter),
                orderBy("date", "desc")
            );
            const servicesSnapshot = await getDocs(servicesQuery);
            const servicesArray: SundayServiceWithId[] = [];
            // One broken document must not empty the whole list (GOD-59): skip it and say so.
            const skipped: string[] = [];
            servicesSnapshot.forEach((doc) => {
                try {
                    servicesArray.push({ ...doc.data(), id: doc.id });
                } catch (err) {
                    skipped.push(doc.id);
                    logger.error("ServiceList.readService", err);
                }
            });
            setServices(servicesArray);
            if (skipped.length > 0) {
                setVariant("warning");
                setMessage(t("simpleSundayService.skippedBroken", { count: skipped.length, ids: skipped.join(", ") }));
                setShowMessage(true);
            }
        } catch (err) {
            logger.error("ServiceList.load", err);
            setVariant("danger");
            setMessage(String(err));
            setShowMessage(true);
        } finally {
            setLoading(false);
        }
    }, [authLoading, isAuthenticated, navigate, setVariant, setMessage, setShowMessage, activeGemeindeId, canEdit, t]);

    useEffect(() => { loadData(); }, [loadData]);

    async function handleDeleteConfirm() {
        if (!serviceToDelete || !activeGemeindeId) return;
        try {
            await deleteItemFromFirestore(activeGemeindeId, Globals.SUNDAY_SERVICE_SUBCOLLECTION, serviceToDelete);
            setVariant("success");
            setMessage(t("simpleSundayService.deleted"));
            setShowMessage(true);
            setShowDeleteModal(false);
            setServiceToDelete(null);
            loadData();
        } catch (err) {
            setVariant("danger");
            setMessage(String(err));
            setShowMessage(true);
        }
    }

    const preacherMap = useMemo(() => {
        const map = new Map<string, string>();
        preachers.forEach((p) => map.set(p.key, `${p.name} ${p.surname}`));
        return map;
    }, [preachers]);

    function getPreacherName(key: string): string {
        return preacherDisplayName(key, preacherMap, t("common.deletedPreacher"));
    }

    function getServiceLabel(service: SundayServiceWithId): string {
        if (service.serviceLabel?.trim()) return service.serviceLabel;
        // One place for the type names: i18n "serviceTypes.*" (GOD-49).
        return t(`serviceTypes.${service.serviceType || "manual"}`, { defaultValue: t("serviceTypes.manual") }) as string;
    }

    function getSlotModeLabel(mode: string | undefined): string {
        const key = mode === "intro" || mode === "reminder" ? mode : "sermon";
        return t(`serviceList.slotModes.${key}`) as string;
    }

    function isReminderMode(mode: string | undefined) {
        return mode === "intro" || mode === "reminder";
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const today = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);

    function isUpcoming(service: SundayServiceWithId): boolean {
        const d = new Date(service.date);
        d.setHours(0, 0, 0, 0);
        return d >= today;
    }

    function isToday(service: SundayServiceWithId): boolean {
        const d = new Date(service.date);
        d.setHours(0, 0, 0, 0);
        return d.getTime() === today.getTime();
    }

    function isNextService(service: SundayServiceWithId, index: number): boolean {
        if (index !== 0) return false;
        return isUpcoming(service);
    }

    function getConfirmationBadge(confirmed: boolean) {
        return confirmed
            ? <span className="gl-badge gl-badge--ok">{t("simpleSundayService.confirmed")}</span>
            : <span className="gl-badge gl-badge--warn">{t("simpleSundayService.pendingConfirmation")}</span>;
    }

    // Group services by month
    type MonthGroup = { key: string; label: string; services: SundayServiceWithId[]; isPast: boolean };
    const monthGroups = useMemo((): MonthGroup[] => {
        const groups: Map<string, MonthGroup> = new Map();
        services.forEach((s) => {
            const d = new Date(s.date);
            const key = `${d.getFullYear()}-${d.getMonth()}`;
            if (!groups.has(key)) {
                const isPast = d < new Date(today.getFullYear(), today.getMonth(), 1);
                groups.set(key, {
                    key,
                    label: d.toLocaleDateString(locale, { month: "long", year: "numeric" }),
                    services: [],
                    isPast,
                });
            }
            groups.get(key)!.services.push(s);
        });
        return Array.from(groups.values());
    }, [services, today, locale]);

    // Count upcoming services for stats
    const upcomingCount = services.filter(isUpcoming).length;
    const confirmedCount = services.filter(
        (s) => isUpcoming(s) && (s.sermon1ConfirmedAt || s.sermon2ConfirmedAt)
    ).length;
    const pendingCount = services.filter(
        (s) => isUpcoming(s) && isReminderMode(s.sermon1Mode) && !s.sermon1ConfirmedAt
    ).length;
    const nextService = services.find(isUpcoming);

    if (loading) {
        return (
            <div className="text-center my-5">
                <Spinner animation="border" />
                <p className="gl-meta mt-2">{t("common.loading")}</p>
            </div>
        );
    }

    // width 100 %: with auto margins a flex item is only as wide as its content and overflowed on phones (GOD-62).
    return (
        <div style={{ width: "100%", maxWidth: 1040, margin: "0 auto", padding: "2rem 1.5rem 3rem" }}>
            {/* Header */}
            <div className="d-flex align-items-end justify-content-between flex-wrap gap-3 mb-4">
                <div>
                    <div className="gl-meta mb-1">{t("serviceList.overview")}</div>
                    <h1 className="mb-0">{t("simpleSundayService.listHeading")}</h1>
                </div>
                <div className="d-flex gap-2 align-items-center flex-wrap">
                    <div className="btn-group" role="group">
                        <Button
                            size="sm"
                            variant={viewMode === "list" ? "primary" : "outline-primary"}
                            onClick={() => setViewMode("list")}
                        >
                            {t("calendar.listView")}
                        </Button>
                        <Button
                            size="sm"
                            variant={viewMode === "calendar" ? "primary" : "outline-primary"}
                            onClick={() => setViewMode("calendar")}
                        >
                            {t("calendar.calendarView")}
                        </Button>
                    </div>
                    {canEdit && (
                        <Button
                            size="sm"
                            variant="primary"
                            onClick={() => navigate(Globals.PATH_SIMPLE_SUNDAY_SERVICE)}
                        >
                            + {t("simpleSundayService.addNew")}
                        </Button>
                    )}
                    {canEdit && (
                        <Button size="sm" variant="outline-primary" onClick={() => setShowSeries(true)} data-testid="open-series">
                            + {t("series.open")}
                        </Button>
                    )}
                </div>
            </div>

            {canEdit && activeGemeindeId && (
                <ServiceSeriesDialog
                    show={showSeries}
                    gemeindeId={activeGemeindeId}
                    services={services}
                    onClose={() => setShowSeries(false)}
                    onCreated={(count) => {
                        setShowSeries(false);
                        setVariant("success");
                        setMessage(t("series.created", { count }));
                        setShowMessage(true);
                        loadData();
                    }}
                />
            )}

            {canEdit && activeGemeindeId && (
                <SwapApprovals
                    gemeindeId={activeGemeindeId}
                    onChanged={loadData}
                    onError={(message) => {
                        setVariant("danger");
                        setMessage(message);
                        setShowMessage(true);
                    }}
                />
            )}

            {/* Stats strip */}
            {viewMode === "list" && services.length > 0 && (
                <div className="row g-3 mb-4">
                    <div className="col-6 col-md-3">
                        <div className="gl-stat">
                            <div className="gl-stat-label">{t("serviceList.statThisMonth")}</div>
                            <div className="gl-stat-value">{upcomingCount}</div>
                            <div className="gl-stat-delta">{t("serviceList.statServices")}</div>
                        </div>
                    </div>
                    <div className="col-6 col-md-3">
                        <div className="gl-stat">
                            <div className="gl-stat-label">{t("serviceList.statConfirmed")}</div>
                            <div className="gl-stat-value" style={{ color: "var(--gl-ok)" }}>{confirmedCount}</div>
                            <div className="gl-stat-delta">{t("serviceList.statPreachers")}</div>
                        </div>
                    </div>
                    <div className="col-6 col-md-3">
                        <div className="gl-stat">
                            <div className="gl-stat-label">{t("serviceList.statPending")}</div>
                            <div className="gl-stat-value" style={{ color: "var(--gl-warn)" }}>{pendingCount}</div>
                            <div className="gl-stat-delta">{t("serviceList.statReminderSent")}</div>
                        </div>
                    </div>
                    <div className="col-6 col-md-3">
                        <div className="gl-stat">
                            <div className="gl-stat-label">{t("serviceList.statNext")}</div>
                            {nextService ? (
                                <>
                                    <div className="gl-stat-value gl-mono" style={{ fontSize: "1.1rem" }}>
                                        {new Date(nextService.date).toLocaleDateString(locale, { day: "numeric", month: "short" })}
                                    </div>
                                    <div className="gl-stat-delta">{getServiceLabel(nextService)}</div>
                                </>
                            ) : (
                                <div className="gl-stat-value" style={{ fontSize: "1rem" }}>—</div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {viewMode === "calendar" ? (
                <CalendarView
                    services={services}
                    preachers={preachers}
                    currentMonth={currentMonth}
                    holidays={holidays}
                    onPreviousMonth={() => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}
                    onNextMonth={() => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}
                    selectedDate={selectedDate}
                    onSelectDate={(date) => setSelectedDate(prev => {
                        if (prev && prev.getTime() === date.getTime()) return prev;
                        return date;
                    })}
                    onAddServiceForDate={
                        canEdit
                            ? (date) => {
                                  const y = date.getFullYear();
                                  const m = String(date.getMonth() + 1).padStart(2, "0");
                                  const d = String(date.getDate()).padStart(2, "0");
                                  navigate(Globals.PATH_SIMPLE_SUNDAY_SERVICE, { state: { selectedDate: `${y}-${m}-${d}` } });
                              }
                            : undefined
                    }
                    onEditService={(s) =>
                        canEdit
                            ? navigate(Globals.PATH_SIMPLE_SUNDAY_SERVICE, { state: { service: s } })
                            : setDetailService(s)
                    }
                />
            ) : services.length === 0 ? (
                <div className="card text-center p-5">
                    <p className="text-muted mb-3">{t("simpleSundayService.noServices")}</p>
                    {canEdit && (
                        <Button variant="primary" onClick={() => navigate(Globals.PATH_SIMPLE_SUNDAY_SERVICE)}>
                            + {t("simpleSundayService.addNew")}
                        </Button>
                    )}
                </div>
            ) : (
                <div>
                    {monthGroups.map((group, gi) => (
                        <div key={group.key}>
                            <div className="d-flex align-items-center justify-content-between mb-2 mt-4" style={{ marginTop: gi === 0 ? 0 : undefined }}>
                                <h6 className="m-0" style={{ color: group.isPast ? "var(--gl-mute)" : "var(--gl-ink)" }}>
                                    {group.label}
                                </h6>
                                {group.isPast && <span className="gl-meta">{t("serviceList.archive")}</span>}
                                {!group.isPast && (
                                    <span className="gl-meta">{t("serviceList.appointments", { count: group.services.length })}</span>
                                )}
                            </div>

                            {group.services.map((service, si) => {
                                const date = new Date(service.date);
                                const past = !isUpcoming(service);
                                const todayService = isToday(service);
                                const nextUp = isNextService(service, services.indexOf(service));

                                const s1Name = getPreacherName(service.sermon1PreacherKey);
                                const s2Name = service.sermon2PreacherKey ? getPreacherName(service.sermon2PreacherKey) : null;
                                const s1Label = getSlotModeLabel(service.sermon1Mode);
                                const s2Label = s2Name ? getSlotModeLabel(service.sermon2Mode) : null;
                                // Slots whose preacher declined or asked a colleague to swap.
                                const slotAlerts = ([1, 2] as const).flatMap((slot) => {
                                    const alert = getSlotAlert(service, slot);
                                    if (!alert) return [];
                                    const key = slot === 1 ? service.sermon1PreacherKey : service.sermon2PreacherKey;
                                    return [{ slot, alert, name: getPreacherName(key), reason: getDeclineReason(service, slot) }];
                                });
                                // Scheduled preachers who entered an absence for this day.
                                const absentSlots = [service.sermon1PreacherKey, service.sermon2PreacherKey].flatMap((key, index) => {
                                    const absence = findAbsence(absences, key, date);
                                    if (!absence || getSlotAlert(service, index === 0 ? 1 : 2) === "declined") return [];
                                    return [{ key, name: getPreacherName(key), absence }];
                                });
                                const s1IsReminder = isReminderMode(service.sermon1Mode);
                                const s2IsReminder = s2Name ? isReminderMode(service.sermon2Mode) : false;

                                const songCount = [
                                    service.choirSong1Name,
                                    service.choirSong2Name,
                                    service.choirSong3Name,
                                    service.choirSong4Name,
                                ].filter(Boolean).length;

                                // Prayer concerns: a single one (Mi/Sa/Sondergottesdienst) or a list
                                // (Sonntag Nachmittag im Gebetsmodus).
                                const prayerItems = [
                                    ...(service.prayerConcern?.trim() ? [service.prayerConcern.trim()] : []),
                                    ...(service.prayerConcerns ?? []).filter((c) => c && c.trim()),
                                ];
                                const isAfternoonPrayer =
                                    service.serviceType === "sunday-afternoon" && service.afternoonMode === "prayer";
                                // The Gemeinde's choir service types, special services, and any service that has songs (GOD-49).
                                const canHaveChoir = canHaveChoirFor(
                                    service.serviceType,
                                    choirTypes,
                                    !!(service.choirSong1Name || service.choirSong2Name || service.choirSong3Name || service.choirSong4Name)
                                );

                                return (
                                    <div
                                        key={service.id}
                                        className="gl-card-row"
                                        style={{ opacity: past ? 0.78 : 1 }}
                                    >
                                        {/* Date plate */}
                                        <div className={`gl-date-plate${todayService || nextUp ? " is-highlight" : ""}`}>
                                            <div className="gl-date-month">{shortName(date, "month")}</div>
                                            <div className="gl-date-day">{date.getDate()}</div>
                                            <div className="gl-date-weekday">{shortName(date, "weekday")}</div>
                                        </div>

                                        {/* Content */}
                                        <div className="gl-card-body">
                                            <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                                                <span className="fw-semibold">{getServiceLabel(service)}</span>
                                                {todayService && <span className="gl-badge gl-badge--accent">{t("serviceList.badgeToday")}</span>}
                                                {nextUp && !todayService && (
                                                    <span className="gl-badge gl-badge--accent">{t("serviceList.badgeNext")}</span>
                                                )}
                                                {past && <span className="gl-badge gl-badge--neutral no-dot">{t("serviceList.badgePast")}</span>}
                                            </div>

                                            {!past ? (
                                                isAfternoonPrayer ? (
                                                    <div className="mb-2">
                                                        <div className="gl-meta mb-1">
                                                            🙏 {t("serviceForm.afternoonModePrayer")} ({prayerItems.length})
                                                        </div>
                                                        {prayerItems.length > 0 && (
                                                            <div className="text-muted small">{prayerItems.join(" · ")}</div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div className="row g-2 mb-2">
                                                        {service.sermon1PreacherKey && (
                                                            <div className="col-12 col-sm-6">
                                                                <div className="gl-meta mb-1">{s1Label} · {s1Name}</div>
                                                                {service.sermon1BiblePassage && (
                                                                    <div>
                                                                        <span className="gl-mono">{service.sermon1BiblePassage}</span>
                                                                        {service.sermon1Topic && (
                                                                            <span className="text-muted"> · {service.sermon1Topic}</span>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                        {s2Name && (
                                                            <div className="col-12 col-sm-6">
                                                                <div className="gl-meta mb-1">{s2Label} · {s2Name}</div>
                                                                {service.sermon2BiblePassage && (
                                                                    <div>
                                                                        <span className="gl-mono">{service.sermon2BiblePassage}</span>
                                                                        {service.sermon2Topic && (
                                                                            <span className="text-muted"> · {service.sermon2Topic}</span>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                        {prayerItems.length > 0 && (
                                                            <div className="col-12">
                                                                <div className="gl-meta">
                                                                    🙏 {t("serviceForm.prayerConcern")}: {prayerItems.join(" · ")}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )
                                            ) : (
                                                <div className="text-muted small mb-1">
                                                    {s1Name}
                                                    {service.sermon1BiblePassage && (
                                                        <> · <span className="gl-mono">{service.sermon1BiblePassage}</span></>
                                                    )}
                                                    {service.sermon1Topic && <> · {service.sermon1Topic}</>}
                                                </div>
                                            )}

                                            {!past && absentSlots.length > 0 && (
                                                <div className="d-flex gap-2 align-items-center flex-wrap mb-1">
                                                    {absentSlots.map(({ key, name, absence }) => (
                                                        <span key={key} className="d-inline-flex gap-2 align-items-center flex-wrap">
                                                            <span className="gl-badge gl-badge--danger">{t("absences.badge", { name })}</span>
                                                            <span className="small text-muted">
                                                                {formatAbsence(absence, t("calendar.locale") as string)}
                                                                {absence.note && ` · ${absence.note}`}
                                                            </span>
                                                        </span>
                                                    ))}
                                                </div>
                                            )}

                                            {!past && slotAlerts.length > 0 && (
                                                <div className="d-flex gap-2 align-items-center flex-wrap mb-1">
                                                    {slotAlerts.map(({ slot, alert, name, reason }) => (
                                                        <span key={slot} className="d-inline-flex gap-2 align-items-center flex-wrap">
                                                            <span className={`gl-badge ${alert === "declined" ? "gl-badge--danger" : "gl-badge--warn"}`}>
                                                                {t(alert === "declined" ? "swap.badgeDeclined" : "swap.badgeSwapPending", { name })}
                                                            </span>
                                                            {reason && <span className="small text-muted">„{reason}“</span>}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}

                                            {!past && (s1IsReminder || s2IsReminder || canHaveChoir) && (
                                                <div className="d-flex gap-2 align-items-center flex-wrap">
                                                    {s1IsReminder && getConfirmationBadge(!!service.sermon1ConfirmedAt)}
                                                    {s2IsReminder && getConfirmationBadge(!!service.sermon2ConfirmedAt)}
                                                    {canHaveChoir && (
                                                        <span className="gl-meta ms-auto">
                                                            {songCount > 0 ? t("serviceList.songs", { count: songCount }) : t("serviceList.noSongs")}
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {/* Actions */}
                                        {canEdit && (
                                            <div className="gl-card-actions">
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    onClick={() => navigate(Globals.PATH_SIMPLE_SUNDAY_SERVICE, { state: { service } })}
                                                >
                                                    {t("simpleSundayService.edit")}
                                                </Button>
                                                <Button
                                                    variant="danger-soft"
                                                    size="sm"
                                                    className="btn-danger-soft"
                                                    onClick={() => { setServiceToDelete(service.id); setShowDeleteModal(true); }}
                                                >
                                                    {t("simpleSundayService.delete")}
                                                </Button>
                                            </div>
                                        )}
                                        {!canEdit && (
                                            <div className="gl-card-actions">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="btn-ghost"
                                                    onClick={() => setDetailService(service)}
                                                    data-testid="view-service"
                                                >
                                                    {t("serviceDetail.view")}
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            )}

            <ServiceDetailModal
                service={detailService}
                label={detailService ? getServiceLabel(detailService) : ""}
                preacherName={getPreacherName}
                slotModeLabel={getSlotModeLabel}
                onClose={() => setDetailService(null)}
            />
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{t("simpleSundayService.confirmDelete")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>{t("simpleSundayService.confirmDeleteMessage")}</Modal.Body>
                <Modal.Footer>
                    <Button variant="ghost" className="btn-ghost" onClick={() => setShowDeleteModal(false)}>
                        {t("common.cancel")}
                    </Button>
                    <Button variant="danger-soft" className="btn-danger-soft" onClick={handleDeleteConfirm}>
                        {t("simpleSundayService.delete")}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
